import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import type { StatusFinanceiro } from "@/modules/obras";
import { Dropdown, FormField } from "@/ds";
import {
  DESCRICAO_TOOLTIP,
  ETAPA_OBRA_SUBTITLE,
  ETAPA_OBRA_TITLE,
  FIELD_LABELS,
  REQUIRED_FIELDS_NOTE,
  SECTION_DETALHES,
  SECTION_IDENTIFICACAO,
  SECTION_STATUS_CONDICOES,
  SELECT_PLACEHOLDER,
  STATUS_FINANCEIRO_TOOLTIP,
  TROCAR_EMBARCACAO_DIALOG,
} from "../nova-obra.constants";
import { ConfirmacaoDialog } from "../components/confirmacao-dialog";
import { InfoEmbarcacao } from "./components/info-embarcacao";
import { LimitesClasseDialog } from "../components/limites-classe-dialog";
import { Field } from "./components/field";
import styles from "./etapa-obra.module.scss";
import { DESCRICAO_MAX_LENGTH, OBSERVACOES_MAX_LENGTH } from "./etapa-obra.constants";
import type { EtapaObraViewModel } from "./types-etapa-obra";

/** Altura da lista de Status da Obra (Figma: 8 itens de 28px, gap 2 e padding 3,5 = 245px, sem rolagem). */
const STATUS_OBRA_LISTA_ALTURA = "245px";

/** Etapa 1 "Dados da Obra" (HU 2519): estado no store do wizard, validação junto ao campo. */
export function EtapaObraView({
  errosVisiveis,
  atualizarObra,
  alternarManutencaoCorretiva,
  obra,
  verLimites,
  abrirLimites,
  fecharLimites,
  confirmandoTroca,
  cancelarTroca,
  informacoes,
  embarcacaoOptions,
  localOptions,
  statusOptions,
  handleEmbarcacaoChange,
  confirmarTroca,
  consultaComFalha,
  financeiroDesabilitado,
  statusFinanceiroOptions,
  periodoObra,
}: EtapaObraViewModel) {
  return (
    <div className={styles.body} data-testid="etapa-obra">
      {consultaComFalha && (
        <div className={styles.alert} role="alert">
          {consultaComFalha.errorMessage}
        </div>
      )}

      <header className={styles.header}>
        <h2 className={styles.title}>{ETAPA_OBRA_TITLE}</h2>
        <p className={styles.subtitle}>{ETAPA_OBRA_SUBTITLE}</p>
        <p className={styles.required}>{REQUIRED_FIELDS_NOTE}</p>
      </header>

      <section className={styles.section} aria-labelledby="secao-identificacao">
        <h3 id="secao-identificacao" className={styles.sectionTitle}>
          {SECTION_IDENTIFICACAO}
        </h3>
        <div className={styles.pair}>
          <Field label={FIELD_LABELS.embarcacao} error={errosVisiveis.embarcacaoId}>
            <Dropdown
              value={obra.embarcacaoId}
              options={embarcacaoOptions}
              onChange={(event) => handleEmbarcacaoChange(event.value ?? null)}
              placeholder={SELECT_PLACEHOLDER}
              filter
              filterPlaceholder=""
              className={styles.select}
              data-testid="obra-embarcacao"
            />
          </Field>
          <Field label={FIELD_LABELS.localPlanejado}>
            <Dropdown
              value={obra.localPlanejadoId}
              options={localOptions}
              onChange={(event) => atualizarObra({ localPlanejadoId: event.value ?? null })}
              placeholder={SELECT_PLACEHOLDER}
              filter
              filterPlaceholder=""
              className={styles.select}
              data-testid="obra-local"
            />
          </Field>
        </div>
        <InfoEmbarcacao
          embarcacaoId={obra.embarcacaoId}
          query={informacoes}
          onVerTodosLimites={abrirLimites}
        />
        {verLimites && obra.embarcacaoId && (
          <LimitesClasseDialog
            embarcacaoId={obra.embarcacaoId}
            periodo={periodoObra}
            onClose={fecharLimites}
          />
        )}
      </section>

      <hr className={`${styles.divider} ${styles.dividerDuplo}`} />

      <section className={styles.section} aria-labelledby="secao-status">
        <h3 id="secao-status" className={styles.sectionTitle}>
          {SECTION_STATUS_CONDICOES}
        </h3>
        <FormField.Root
          labelPosition="left"
          className={styles.switchRow}
          classNames={{ label: styles.switchLabel }}
        >
          <FormField.Label>{FIELD_LABELS.manutencaoCorretiva}</FormField.Label>
          <FormField.Control>
            <InputSwitch
              checked={obra.manutencaoCorretiva}
              onChange={(event) => alternarManutencaoCorretiva(Boolean(event.value))}
              data-testid="obra-manutencao-corretiva"
            />
          </FormField.Control>
        </FormField.Root>
        <div className={styles.pair}>
          <Field label={FIELD_LABELS.statusObra} error={errosVisiveis.statusObraId}>
            <Dropdown
              value={obra.statusObraId}
              options={statusOptions}
              onChange={(event) => atualizarObra({ statusObraId: event.value ?? null })}
              placeholder={SELECT_PLACEHOLDER}
              className={styles.select}
              scrollHeight={STATUS_OBRA_LISTA_ALTURA}
              data-testid="obra-status-obra"
            />
          </Field>
          <Field
            label={FIELD_LABELS.statusFinanceiro}
            error={errosVisiveis.statusFinanceiro}
            disabled={financeiroDesabilitado}
            hint={{
              text: STATUS_FINANCEIRO_TOOLTIP,
              testId: "hint-status-financeiro",
              tooltipClassName: styles.tooltipStatusFinanceiro,
            }}
          >
            <Dropdown
              value={obra.statusFinanceiro}
              options={statusFinanceiroOptions}
              onChange={(event) =>
                atualizarObra({
                  statusFinanceiro: (event.value ?? null) as StatusFinanceiro | null,
                })
              }
              placeholder={SELECT_PLACEHOLDER}
              className={styles.select}
              data-testid="obra-status-financeiro"
            />
          </Field>
        </div>
      </section>

      <hr className={styles.divider} />

      <section className={styles.section} aria-labelledby="secao-detalhes">
        <h3 id="secao-detalhes" className={styles.sectionTitle}>
          {SECTION_DETALHES}
        </h3>
        <Field label={FIELD_LABELS.observacoes} error={errosVisiveis.observacoes} wide>
          <InputText
            value={obra.observacoes}
            maxLength={OBSERVACOES_MAX_LENGTH}
            onChange={(event) => atualizarObra({ observacoes: event.target.value })}
            className={styles.input}
          />
        </Field>
        <Field
          label={FIELD_LABELS.descricao}
          error={errosVisiveis.descricao}
          hint={{
            text: DESCRICAO_TOOLTIP,
            testId: "hint-descricao",
            tooltipClassName: styles.tooltipDescricao,
          }}
          wide
        >
          <InputTextarea
            value={obra.descricao}
            maxLength={DESCRICAO_MAX_LENGTH}
            rows={4}
            onChange={(event) => atualizarObra({ descricao: event.target.value })}
            className={`${styles.input} ${styles.textarea}`}
          />
        </Field>
      </section>

      <ConfirmacaoDialog
        visible={confirmandoTroca}
        header={TROCAR_EMBARCACAO_DIALOG.title}
        message={TROCAR_EMBARCACAO_DIALOG.text}
        acceptLabel={TROCAR_EMBARCACAO_DIALOG.confirm}
        rejectLabel={TROCAR_EMBARCACAO_DIALOG.cancel}
        accept={confirmarTroca}
        onHide={cancelarTroca}
        testId="confirmar-troca-embarcacao"
      />
    </div>
  );
}
